import {
  conversationQueryKey,
  useMutationRenameConversation,
} from "@apis:conversation/conversation";
import { Message } from "@apis:message/message";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import {
  Box,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Tab } from "@pages:app/talk/const";
import { useSearchActions } from "@stores:app/talk/search";
import { useToasterStoreActions } from "@stores:global";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdOutlineEdit, MdOutlineMoreHoriz } from "react-icons/md";
import { useNavigate, useParams } from "react-router";

import { FeatureFlag } from "@:flag";

const TITLE_MAX_LENGTH = 64;

export const HistoryItem = ({
  message,
  title,
  hoveredConversationId,
  setHoveredConversationId,
  tab,
}: {
  message: Message["GET_ONE"]["Response"];
  title: string;
  hoveredConversationId: string | undefined;
  setHoveredConversationId: (value?: string) => void;
  tab?: Tab;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const currentConvoId = useMemo(
    () => params.conversationId,
    [params.conversationId],
  );
  const searchActions = useSearchActions();
  const invalidateQueries = useInvalidateQueries();

  const isConversationActive = currentConvoId === message.conversationId;
  const isConversationHovered =
    hoveredConversationId === message.conversationId;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [isRenaming, setIsRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const isCommittingRename = useRef(false);
  const originalTitleRef = useRef("");
  const isRenameCancelled = useRef(false);
  const renameConversation = useMutationRenameConversation();
  const toastActions = useToasterStoreActions();

  const commitRename = async () => {
    setIsRenaming(false);

    // Escape was pressed: skip the blur-triggered commit
    if (isRenameCancelled.current) {
      isRenameCancelled.current = false;
      return;
    }

    const newTitle = draftTitle.trim();
    if (newTitle === originalTitleRef.current || isCommittingRename.current) {
      return;
    }

    isCommittingRename.current = true;
    try {
      // An empty title clears the rename, falling back to the generated titles
      await renameConversation.mutateAsync({
        id: message.conversationId as string,
        userTitle: newTitle || null,
      });

      // The refetch also propagates the new title to the browser tab
      await invalidateQueries.run([
        { queryKey: conversationQueryKey.getAll.queryKey },
      ]);
    } catch {
      toastActions.setToaster({
        open: true,
        variant: "error",
        message: t("talk.renameFailed"),
      });

      // Reopen the editor so the draft is not lost
      setIsRenaming(true);
    } finally {
      isCommittingRename.current = false;
    }
  };

  return (
    <Box
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        if ((e.target as any).classList.contains("MuiModal-backdrop")) {
          return;
        }

        if (isRenaming) {
          return;
        }

        if (tab === Tab.Chat) {
          if (currentConvoId === message?.conversationId) {
            return;
          }

          navigate(`/app/chat/${message?.conversationId}`);
        }

        if (tab === Tab.Search) {
          searchActions.resetAnswer();
          searchActions.setQuestion(message.question);
        }
      }}
      sx={{
        marginTop: "4px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "5px",
        cursor: "pointer",
        paddingLeft: "20px",
        "&:hover, &.selected": {
          backgroundColor: "white",
        },
        backgroundColor: isConversationActive ? "white" : undefined,
      }}
      onMouseEnter={() =>
        setHoveredConversationId(message?.conversationId as string)
      }
      onMouseLeave={() => setHoveredConversationId(undefined)}
    >
      <Box
        sx={{
          display: "flex",
          paddingY: "8px",
          gap: "8px",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        <SVGInternalIcon
          icon="chatMessageIcon"
          sx={{
            width: "16px",
            height: "16px",
          }}
        />
        {isRenaming ? (
          <InputBase
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              }
              if (e.key === "Escape") {
                isRenameCancelled.current = true;
                setIsRenaming(false);
              }
            }}
            onBlur={() => commitRename()}
            inputProps={{ maxLength: TITLE_MAX_LENGTH }}
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: 400,
              letterSpacing: "0.15px",
              flex: 1,
              minWidth: 0,
              backgroundColor: "white",
              borderRadius: "4px",
              paddingX: "4px",
            }}
          />
        ) : (
          <Tooltip title={title} placement="bottom">
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 400,
                letterSpacing: "0.15px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {(isConversationHovered || open) &&
        (tab === Tab.Chat || FeatureFlag.DeleteMessageHistoryTalk) && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              zIndex: 20,
              marginRight: "10px",
            }}
          >
            <MdOutlineMoreHoriz />
          </IconButton>
        )}

      <Menu
        className="action-menu"
        // Let the rename input keep focus while the menu fades out
        disableEnforceFocus
        disableRestoreFocus
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
          setHoveredConversationId(undefined);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 2,
              width: "123px",
              borderRadius: "15px",
            },
          },
        }}
      >
        {tab === Tab.Chat && (
          <MenuItem
            id="action-rename"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              event.nativeEvent.stopPropagation();

              const initialTitle = title.slice(0, TITLE_MAX_LENGTH);
              setDraftTitle(initialTitle);
              originalTitleRef.current = initialTitle;
              isRenameCancelled.current = false;
              setIsRenaming(true);
              setAnchorEl(null);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "center",
            }}
          >
            <MdOutlineEdit />
            {t("common.rename")}
          </MenuItem>
        )}
        {FeatureFlag.DeleteMessageHistoryTalk && (
          <MenuItem
            id="action-delete"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              event.nativeEvent.stopPropagation();

              if (tab === Tab.Chat) {
                navigate(`/app/chat/new`, { replace: true });
              }
              setAnchorEl(null);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "center",
              color: "error.main",
            }}
          >
            <MdDelete />
            {t("common.delete")}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
